import {api} from "@/shared/api";
import {Integrations} from "../integrations";
import {AddisonEAUConfig, IncapacityReasonMap} from "./addisonEAU";
import {FormUtil} from "@/shared/forms/formUtil";
import {htmlEscape} from "@/shared/utils/string";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Input from "@/shared/utils/input";
import {formatDate, getISODate, parseDateInput} from "@/shared/utils/datefunctions";
import {FieldError, Integration, WorkingTimeType} from "@/shared/timr";
import {IncapacityReason, IntegrationType, WorkingTimeTypeCategory} from "@/shared/timrTypes";

const template = `
	<div id="addisonConfigDialog">
		<form id="addisonConfigForm" class="form-horizontal">
			<div class="form-group">
				<div class="col-xs-12">
					<div class="flex-row justify-left mb-40 form-group">
						<div class="flex-column flex-vertical-center mr-15">
							<label class="text-nowrap mr-20 pt-8" for="addisonClientNr">${messages["integrations.addisonEAU.clientNr"]}</label>
						</div>
						<div class="flex-column">
							<input type="number" class="form-control width-350" id="addisonClientNr" name="clientNr" max="99999999" min="0"/>
						</div>
					</div>
					<div>${messages["integrations.addisonEAU.workingTimeTypesToExport"]}</div>
					<div class="mb-20 panel panel-bordered p-15 pb-10" style="max-height: 300px; overflow-y: auto">
						<div class="flex-row">
							<div class="flex-column-30">${messages["integrations.addisonEAU.workingTimeType"]}</div>
							<div class="flex-column-10 mr-15">${messages["integrations.addisonEAU.export"]}</div>
							<div class="flex-column-60">${messages["integrations.addisonEAU.incapacityReason"]}</div>
						</div>
						<div id="workingTimeTypeContainer">
							<!-- Working time type row -->
						</div>
					</div>
					<div class="flex-row mt-40">
						<div class="flex-column mr-15 pt-8">${messages["integrations.addisonEAU.dateFrom"]}</div>
						<div class="input-group">
							<div class="input-group-btn">
								<label for="addisonDateFrom" class="btn btn-default btn-icon">
									<i class="far fa-calendar-day" ></i>
								</label>
							</div>
							<input type="text" class="form-control daterange-single" name="dateFrom" id="addisonDateFrom">
						</div>
					</div>
					<div class="flex-row mt-40">
						<div class="flex-column mr-15 pt-8">${messages["integrations.addisonEAU.statusAfterExport"]}</div>
						<div class="flex-column">
							<select class="form-control width-150" id="addisonStatusAfterExport" name="statusAfterExport">
								<option value="null">${messages["integrations.addisonEAU.noChange"]}</option>
								<option value="LOCKED">${messages["reports.filter.status.LOCKED"]}</option>
								<option value="CLOSED">${messages["reports.filter.status.CLOSED"]}</option>
							</select>
						</div>
					</div>
				</div>
			</div>
		</form>
	</div>
	`;

export class AddisonEAUConfigDialog extends HTMLElement {
	private static readonly GREY_TEXT_COLOR_CLASS = "text-grey-300";

	private dom: JQuery;
	private modal: BootstrapModal.Modal;

	private editMode: boolean;
	private editIntegration: Integration;

	private getWorkingTimeTypesRequest: JQueryXHR;

	public static create() {
		const dialog = document.createElement("addison-config-dialog") as AddisonEAUConfigDialog;
		$("body").append(dialog);
		dialog.create();
		dialog.remove();
	}

	public static edit(integration: Integration, configuration: AddisonEAUConfig) {
		const dialog = document.createElement("addison-config-dialog") as AddisonEAUConfigDialog;
		$("body").append(dialog);
		dialog.edit(integration, configuration);
		dialog.remove();
	}

	private connectedCallback() {
		this.innerHTML = template;
		Input.initSingleDateRangePickers($(".daterange-single"));

		this.dom = $("#addisonConfigDialog");
		this.modal = new BootstrapModal.Modal({
			title: `<img src="/timr/images/integrations/addison.svg?version=1" width="100px">`,
			escapeText: false,
			message: this.dom,
			size: "modal-lg",
			destroyOnClose: true,
			buttons: [
				{
					text: "dialog.save",
					css: "saveButton btn-primary",
					action: () => {
						if (!this.editMode) {
							Integrations.triggerAddedIntegrationEvent(IntegrationType.ADDISON, this.convertFormToJSONString($("#addisonConfigForm")));
						} else {
							Integrations.triggerEditedIntegrationEvent(IntegrationType.ADDISON, this.editIntegration, this.convertFormToJSONString($("#addisonConfigForm")));
						}
					}
				},
				{
					text: "dialog.cancel",
					css: "cancelButton btn-default"
				},
				{
					text: "dialog.delete",
					css: "deleteButton btn-danger",
					left: true,
					action: (m) => {
						BootstrapModal.confirm({
							title: messages["dialog.delete.confirm"],
							message: messages["integrations.addisonEAU.delete.message"],
							yesAction: (confirmModal) => {
								confirmModal.close();
								m.close();
								Integrations.triggerDeletedIntegrationEvent(IntegrationType.ADDISON, this.editIntegration.id);
							}
						});
					}
				}
			],
			onOpen: (m) => {
				Input.toggle(m.find(".deleteButton"), this.editMode);
				this.dom.find("#addisonClientNr").focus();
				Integrations.onSaveIntegration(IntegrationType.ADDISON, (e, fieldErrors: FieldError[]) => {
					if (!fieldErrors) {
						m.close();
					} else {
						FormUtil.markFieldErrors($("#addisonConfigForm"), fieldErrors);
						m.resetLoadingButtons();
					}
				});
			}
		});
	}

	private create() {
		this.editMode = false;
		this.loadWorkingTimeTypes();
		this.modal.show();
	}

	private edit(editIntegration: Integration, config: AddisonEAUConfig) {
		this.editIntegration = editIntegration;
		this.editMode = true;
		this.initDialog(config);
		this.loadWorkingTimeTypes(config);
		this.modal.show();
	}

	private initDialog(config: AddisonEAUConfig) {
		$("#addisonClientNr").val(config.clientNr);
		$("#addisonStatusAfterExport").val(String(config.statusAfterExport));
		if (config.dateFrom) {
			// DEVNOTE: Only set date if it is not null to auto insert current date
			$("#addisonDateFrom").val(formatDate(config.dateFrom));
		}
	}

	private loadWorkingTimeTypes(config?: AddisonEAUConfig) {
		const params = []
		params.push({name: "archived", value: false});
		params.push({name: "categories", value: [WorkingTimeTypeCategory.PaidTimeOff]});

		this.getWorkingTimeTypesRequest?.abort();
		this.getWorkingTimeTypesRequest = api("admin/workingTimeTypes.json")
			.get(params)
			.done((response: { data: WorkingTimeType[] }) => {
				const workingTimeTypeContainer = this.dom.find("#workingTimeTypeContainer");
				workingTimeTypeContainer.empty();
				response.data.forEach((workingTimeType) => {
					const row = $(this.buildWorkingTimeTypeRow(workingTimeType, config));
					row.find("select[disabled]").addClass(AddisonEAUConfigDialog.GREY_TEXT_COLOR_CLASS);
					workingTimeTypeContainer.append(row);
				});
				this.initListeners();
			});
	}

	private buildWorkingTimeTypeRow(workingTimeType: WorkingTimeType, config?: AddisonEAUConfig) {
		const workingTimeTypeExport = config?.incapacityReasonMap?.has(workingTimeType.id);
		const reason = workingTimeTypeExport ? config?.incapacityReasonMap?.get(workingTimeType.id) : IncapacityReason.ILLNESS;

		return `
				<div class="flex-row mb-10">
					<div class="flex-column-30 pt-8 text-nowrap overflow-ellipsis">${htmlEscape(workingTimeType.name)}</div>
					<div class="flex-column-10 mr-15 pt-8">
						<input name="export" type="checkbox" value="${workingTimeType.id}" ${workingTimeTypeExport ? "checked" : ""}>
					</div>
					<div class="flex-column-60">
						<select class="form-control" ${!workingTimeTypeExport ? "disabled" : ""}>
							<option value="${IncapacityReason.ILLNESS}" ${reason === IncapacityReason.ILLNESS ? "selected" : ""}>${messages["integrations.addisonEAU.incapacityReason.ILLNESS"]}</option>
							<option value="${IncapacityReason.WORK_INCIDENT}" ${reason === IncapacityReason.WORK_INCIDENT ? "selected" : ""}>${messages["integrations.addisonEAU.incapacityReason.WORK_INCIDENT"]}</option>
							<option value="${IncapacityReason.HOSPITAL_STAY}" ${reason === IncapacityReason.HOSPITAL_STAY ? "selected" : ""}>${messages["integrations.addisonEAU.incapacityReason.HOSPITAL_STAY"]}</option>
						</select>
					</div>
				</div>
			`;
	}

	private initListeners() {
		this.dom.find("[name=export]").change((e) => {
			const checkbox = $(e.target);
			const select = checkbox.closest(".flex-row").find("select");
			select.prop("disabled", !checkbox.is(":checked"));
			select.toggleClass(AddisonEAUConfigDialog.GREY_TEXT_COLOR_CLASS, !checkbox.is(":checked"));
		});
	}

	private convertFormToJSONString(form: JQuery): string {
		type ParamsType = { name: string, value: object | string };
		const params: ParamsType[] = [];
		params.push({name: "clientNr", value: form.find("#addisonClientNr").val() as string});
		params.push({name: "statusAfterExport", value: form.find("#addisonStatusAfterExport").val() as IntegrationType});
		params.push({name: "dateFrom", value: getISODate(parseDateInput(form.find("#addisonDateFrom").val() as string))});

		const workingTimeTypesForExport: IncapacityReasonMap = new Map();
		form.find("[type=checkbox]:checked").each((i, e) => {
			const workingTimeTypeId = parseInt($(e).val() as string);
			const reason = $(e).closest(".flex-row").find("select").val() as IncapacityReason;
			workingTimeTypesForExport.set(workingTimeTypeId, reason);
		});

		params.push({name: "incapacityReasonMap", value: this.mapToObj(workingTimeTypesForExport)});

		const json: Record<string, object | string> = {};
		$.each(params, (i, param: ParamsType) => {
			json[param.name] = param.value || "";
		});
		return JSON.stringify(json);
	}

	private mapToObj(map: IncapacityReasonMap): { [key: number]: IncapacityReason } {
		const obj: { [key: number]: IncapacityReason } = {};
		map.forEach((value, key) => {
			obj[key] = value;
		});
		return obj;
	}
}
